import { useEffect, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const useBlendShapeAnimation = (
  scene: THREE.Object3D | undefined,
  blendShapeFilePath: string, // JSON 파일 경로
  frameRate: number,
) => {
  const frameRef = useRef(0);
  const blendShapeDataRef = useRef<any | null>(null);

  useEffect(() => {
    if (!scene || !blendShapeFilePath) return;

    // BlendShape JSON 파일 로드
    fetch(blendShapeFilePath)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Failed to load blendshape.json: ${response.statusText}`,
          );
        }
        return response.json();
      })
      .then((data) => {
        blendShapeDataRef.current = data;
      })
      .catch((error) => {
        console.error('Error loading blendshape.json:', error);
      });
  }, [scene, blendShapeFilePath]);

  useEffect(() => {
    if (!scene) return;

    const meshMap: { [key: string]: THREE.Mesh } = {};
    scene.traverse((object) => {
      if (object instanceof THREE.Mesh && object.morphTargetInfluences) {
        meshMap[object.name] = object;
      }
    });

    const updateBlendShapes = () => {
      if (!blendShapeDataRef.current) return;

      const blendShapeData = blendShapeDataRef.current;

      const totalFrames = Math.max(
        ...Object.values(
          blendShapeData as Record<
            string,
            Record<number, Record<string, number>>
          >,
        ).flatMap((frames) =>
          Object.keys(frames).map((frame) => Number(frame)),
        ),
      );

      const currentFrame = frameRef.current % (totalFrames + 1);

      for (const meshName in blendShapeData) {
        // const meshName1 = meshName.replace('.', '');
        const mesh = meshMap[meshName];
        if (!mesh || !mesh.morphTargetInfluences) continue;

        const frames = blendShapeData[meshName];
        const blendShapeValues = frames[currentFrame];

        if (blendShapeValues) {
          for (const shapeName in blendShapeValues) {
            const index = mesh.morphTargetDictionary?.[shapeName];
            if (index !== undefined) {
              mesh.morphTargetInfluences[index] = blendShapeValues[shapeName];
            }
          }
        }
      }

      frameRef.current++;
    };

    const intervalId = setInterval(updateBlendShapes, 1000 / frameRate);

    return () => clearInterval(intervalId);
  }, [scene, frameRate]);

  useFrame(() => {
    // Optional: 추가적인 애니메이션 처리
  });
};

export default useBlendShapeAnimation;
